import { createContext, useState, useCallback, useEffect } from 'react';

import useAuth from 'hooks/useAuth';
import { getActiveGuidedTour } from 'services/api/organizations';

// Create context
const TourContext = createContext(null);

// Fired by the Advanced Settings → Guided Tour editor after a successful save so
// the live tour (header bulb + runner) re-resolves without a full page reload.
export const GUIDED_TOUR_UPDATED_EVENT = 'ayro:guided-tour-updated';

// Provider component
export function TourProvider({ children }) {
  const { user } = useAuth();
  const [runTour, setRunTour] = useState(false);
  const [tourKey, setTourKey] = useState(0);

  // The resolved guided tour for the signed-in user (see GET /organizations/guided-tour/active).
  //  - backendTour  : an enabled, configured tour to run (null otherwise).
  //  - suppressed   : an admin turned the tour OFF at the nearest configured level;
  //                   show NO tour and hide the header bulb (NOT the built-in default).
  //  - tourResolved : the backend has answered, so consumers can stop waiting.
  const [backendTour, setBackendTour] = useState(null);
  const [suppressed, setSuppressed] = useState(false);
  const [tourResolved, setTourResolved] = useState(false);

  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    setTourResolved(false);
    setBackendTour(null);
    setSuppressed(false);
    getActiveGuidedTour()
      .then((t) => {
        if (cancelled) return;
        // The backend only reports enabled:true when the tour has something to
        // show — ordered steps OR an intro page. So an enabled tour (even with
        // zero steps but an intro title) is delivered here.
        if (t?.enabled) {
          setBackendTour({ ...t, steps: Array.isArray(t.steps) ? t.steps : [] });
        } else if (t?.configured) {
          // Configured-and-off at the nearest level — suppress the tour entirely.
          setSuppressed(true);
        }
      })
      .catch(() => {
        // Not reachable — no tour (the feature is fully config-driven).
      })
      .finally(() => {
        if (!cancelled) setTourResolved(true);
      });
    return () => {
      cancelled = true;
    };
  }, [user, reloadKey]);

  // Re-resolve when an admin saves the tour config, so enabling/disabling reflects
  // immediately (same tab) instead of only after a page reload.
  useEffect(() => {
    const refetch = () => setReloadKey((k) => k + 1);
    window.addEventListener(GUIDED_TOUR_UPDATED_EVENT, refetch);
    return () => window.removeEventListener(GUIDED_TOUR_UPDATED_EVENT, refetch);
  }, []);

  const startTour = useCallback(() => {
    // Remove completed flag
    localStorage.removeItem('guided_tour_completed');
    // Increment key to force re-render of tour component
    setTourKey((prev) => prev + 1);
    // Start the tour
    setRunTour(true);
  }, []);

  const stopTour = useCallback(() => {
    setRunTour(false);
  }, []);

  return (
    <TourContext.Provider value={{ runTour, tourKey, startTour, stopTour, backendTour, suppressed, tourResolved }}>
      {children}
    </TourContext.Provider>
  );
}

export default TourContext;
